/**
 * MembershipCertificateSection — the Jäsentodistus in-page view (T029).
 *
 * Displays the certificate fields (print date, name, member number, start date,
 * fee status) and an obtain-document action that downloads the certificate.
 *
 * Grounded in the proto's Jäsentodistus section (Profile.tsx / Jasentiedot.tsx):
 * a labelled field stack + a "Lataa jäsentodistus" button. As of 049 (D-215),
 * `obtainDocument()` calls the server-side `member.certificate` mutation and
 * downloads a REAL PDF (`pdf-lib`, rendered in `apps/api`) — the proto's
 * client-side jsPDF and the interim demo `.txt` are both retired; 026's RD-006
 * "avoid a heavy client-side document dependency" concern is honoured by
 * keeping the PDF library server-side (research.md Decision 1), not by
 * avoiding a PDF altogether. This consumer triggers the download via a
 * transient <a download> and then revokes the object URL (the port contract
 * puts URL lifecycle on the consumer).
 *
 * FR-008: if generation fails (a missing/unresolved required field on the
 * server, or the surface being unreachable), `downloadFailed` renders a
 * clear, non-technical localized message — no partial or fabricated document.
 * A blocked or failed PREVIEW (O-021) never reaches this component: the route's
 * PortErrorBoundary renders SectionUnavailable (`profile.certificate.unavailable`).
 *
 * Consumes the membershipCertificate port via Suspense (useMembershipCertificate
 * caches the read). Mount inside a Suspense boundary.
 *
 * No raw colour literals (FR-013) — token classes only. Mobile-first, 375 px
 * safe (FR-012).
 *
 * Owned by feature 026-proto-convergence-v2; reshaped by
 * 049-membership-certificate (D-215).
 * Task: T029
 */

import { type JSX, useState } from 'react';
import { useLanguage } from '../../i18n/useLanguage.js';
import { useTranslate } from '../../i18n/useTranslate.js';
import { isPermanentCertificateBlock } from '../../membership-certificate/certificateBlockReason.js';
import type { CertificateDocument } from '../../membership-certificate/MembershipCertificatePort.js';
import { useMembershipCertificate } from '../../membership-certificate/useMembershipCertificate.js';
import { CalendarIcon, CheckIcon, HashIcon, ProfileIcon } from '../../shell/icons.js';
import { formatIsoDate } from './formatIsoDate.js';
import { ProfileSectionHeader } from './ProfileSectionHeader.js';

// The back affordance is owned by the route (item-4 r1 C5), so this section takes
// no props — it renders only its content + subordinate heading.

/**
 * Trigger a browser download for a freshly-obtained document, then revoke its
 * object URL. The anchor is created, clicked, and discarded synchronously; the
 * URL is revoked afterwards so the object URL does not leak.
 */
async function triggerDownload(
  obtain: () => CertificateDocument | PromiseLike<CertificateDocument>,
): Promise<void> {
  const document_ = await obtain();
  try {
    const anchor = document.createElement('a');
    anchor.href = document_.url;
    anchor.download = document_.filename;
    // Off-DOM click is sufficient to start a download in browsers; appending is
    // unnecessary and would risk a visible flash.
    anchor.click();
  } finally {
    // The port mints a fresh object URL per call; the consumer owns revocation.
    // Defer it a macrotask so the blob URL stays valid until the download
    // actually starts: some browsers cancel a still-pending download if the URL
    // is revoked in the same tick as the synchronous click(). In a `finally` so
    // a throwing click() still releases the URL (the useExport posture).
    setTimeout(() => {
      URL.revokeObjectURL(document_.url);
    }, 0);
  }
}

// Proto Jäsentodistus rows carry a leading field icon (Profile.tsx:158-182 at
// pin 9393e6f8). Token-tinted via currentColor; the glyph is decorative, the
// adjacent <dt> carries the label.
const fieldIconClasses = 'h-5 w-5 shrink-0 text-muted-foreground';

function CertificateField({
  icon,
  label,
  value,
}: {
  icon: JSX.Element;
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div className="border-b border-border px-4 py-3 last:border-b-0">
      {/* Grouping <div> children must be only <dt>/<dd> (axe definition-list /
          only-dlitems, PR #282): the leading icon lives inside the <dt>. */}
      <dt className="flex items-center gap-3 text-sm font-medium text-muted-foreground">
        {icon}
        {label}
      </dt>
      <dd className="mt-1 pl-8 text-sm text-foreground">{value}</dd>
    </div>
  );
}

export function MembershipCertificateSection(): JSX.Element {
  const t = useTranslate();
  const { active: locale } = useLanguage();
  const { certificate, obtainDocument } = useMembershipCertificate();
  // `null` = no failure. Otherwise which copy the failure earns — see
  // `isPermanentCertificateBlock`. Reachable when the record changes between
  // the preview and the download, or when only the render/audit step fails.
  const [downloadFailure, setDownloadFailure] = useState<'retry' | 'contact-service' | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  // The CERTIFICATE's own wording, not the Jäsenmaksu surface's: this view
  // previews the document, and the document says `Maksettu` / `Maksamatta`
  // (CERTIFICATE_LABELS_FI). Reusing `profile.fee.status*` showed
  // "Ei maksettu" on screen against "Maksamatta" in the PDF — the same
  // state in two vocabularies (P8), the wording half of O-021.
  const feeLabel =
    certificate.feeStatus === 'paid'
      ? t('profile.certificate.feePaid')
      : t('profile.certificate.feeUnpaid');

  const handleDownload = (): void => {
    // Ignore re-clicks while a generation is outstanding: each server round-trip
    // renders a full PDF and writes an audit event, so a stalled/slow request
    // must not let impatient re-clicks pile up duplicate audited generations.
    if (downloading) return;
    setDownloadFailure(null);
    setDownloaded(false);
    setDownloading(true);
    // Surface a visible failure when obtainDocument() rejects — e.g. a
    // server-side FR-008 block from `member.certificate`, a request timeout, or
    // the surface being unreachable — rather than swallowing it.
    void triggerDownload(obtainDocument)
      .then(() => {
        // The terminal success state gets its OWN text: clearing the live
        // region would announce nothing.
        setDownloaded(true);
      })
      .catch((error: unknown) => {
        setDownloadFailure(isPermanentCertificateBlock(error) ? 'contact-service' : 'retry');
      })
      .finally(() => {
        setDownloading(false);
      });
  };

  return (
    <div className="flex flex-col gap-4">
      <ProfileSectionHeader title={t('profile.certificate.heading')} />

      <dl className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card">
        <CertificateField
          icon={<CalendarIcon className={fieldIconClasses} aria-hidden="true" />}
          label={t('profile.certificate.issuedAt')}
          value={formatIsoDate(certificate.printDate, locale)}
        />
        <CertificateField
          icon={<ProfileIcon className={fieldIconClasses} aria-hidden="true" />}
          label={t('profile.certificate.memberName')}
          value={certificate.memberName}
        />
        <CertificateField
          icon={<HashIcon className={fieldIconClasses} aria-hidden="true" />}
          label={t('profile.certificate.memberNumber')}
          value={certificate.memberNumber}
        />
        <CertificateField
          icon={<CalendarIcon className={fieldIconClasses} aria-hidden="true" />}
          label={t('profile.certificate.startDate')}
          value={formatIsoDate(certificate.membershipStartDate, locale)}
        />
        <CertificateField
          icon={<CheckIcon className={fieldIconClasses} aria-hidden="true" />}
          label={t('profile.certificate.feeStatus')}
          value={feeLabel}
        />
      </dl>

      {/* aria-disabled (not native `disabled`) while generating: native
          disabled on the focused button drops keyboard focus to <body>. The
          handleDownload guard enforces the no-re-click rule. */}
      <button
        type="button"
        onClick={handleDownload}
        aria-disabled={downloading}
        aria-busy={downloading}
        className="rounded-lg bg-primary px-4 py-3 font-semibold text-primary-foreground transition-colors hover:bg-primary/90 aria-disabled:cursor-not-allowed aria-disabled:opacity-60"
      >
        {t('profile.certificate.download')}
      </button>

      {/* The live region is always mounted (empty) and only its text changes —
          a region mounted together with its text announces unreliably.
          It also carries the PENDING message: `aria-busy` on the button is not
          announced by NVDA/JAWS/VoiceOver, and a server round-trip that renders
          a PDF can take seconds, so without this the press is silent. Idle it is
          `sr-only` rather than '' so the empty region adds no trailing gap to the
          parent's `gap-4` stack (the ExportDataSection pattern). */}
      <div
        className={
          downloadFailure !== null
            ? 'rounded-lg bg-muted p-3 text-sm text-muted-foreground'
            : downloading || downloaded
              ? 'text-sm text-muted-foreground'
              : 'sr-only'
        }
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {downloadFailure === 'contact-service'
          ? t('profile.certificate.contactService')
          : downloadFailure === 'retry'
            ? t('profile.certificate.downloadError')
            : downloading
              ? t('profile.certificate.downloading')
              : downloaded
                ? t('profile.certificate.downloaded')
                : null}
      </div>
    </div>
  );
}
